import { Alpine } from "alpinejs";
import { getFocusableElements } from "../utils";

interface SliderOptions {
	infinite: boolean | undefined;
}

const defaultOptions: SliderOptions = {
	infinite: true,
};

/**
 * Makes keyboard-inert clone.
 * - tags clone with data attributes (data-clone-of, data-clone)
 * - marks as aria-hidden
 * - removes duplicate IDs
 */
function createGhostClone(el: HTMLElement, index: number): HTMLElement {
	const clone = el.cloneNode(true) as HTMLElement;

	clone.setAttribute("data-clone", "true");
	clone.setAttribute("data-clone-of", String(index));

	clone.setAttribute("aria-hidden", "true");
	clone.setAttribute("tabindex", "-1");

	clone
		.querySelectorAll<HTMLElement>("[id]")
		.forEach((n) => n.removeAttribute("id"));

	getFocusableElements(clone).forEach((n) => {
		n.setAttribute("tabindex", "-1");
		n.setAttribute("aria-hidden", "true");
	});
	return clone;
}

const slider = (options: SliderOptions) => {
	const settings = { ...defaultOptions, ...options };
	return {
		slides: [],

		width: 0,
		trackWidth: 0,
		trackEnd: 0,
		startRePos: 0,
		sliderOffset: 0,
		endRePos: 0,

		dragging: false,
		downPoint: null,
		isFirefox: false,
		mouse: {
			x: 0,
			y: 0,
		},
		prompt: {
			x: 0,
			y: 0,
		},
		promptVisible: false,
		settings,
		frag: [],
		clones: [],

		init(): void {
			this.setPromptPosition();
			const items: HTMLElement = this.$refs.sliderItems;
			if (items.children.length) {
				if (settings.infinite) {
					this.initInfinite();
				} //do first

				this.setSizes(items);
				window.addEventListener("resize", () => {
					this.setSizes(items);
				});

				this.setPosition();

				if (settings.infinite) {
					this.$refs.slider.scrollTo({
						left: this.startRePos - this.sliderOffset,
						behavior: "instant",
					});
				}

				//Thanks Firefox
				this.isFirefox = window.navigator.userAgent.includes("Firefox");
				Array.from(items.children).forEach((e) => {
					Array.from(e.querySelectorAll("a")).forEach((e) => {
						e.setAttribute("draggable", "false");
					});
					Array.from(e.querySelectorAll("img")).forEach((e) => {
						e.style.pointerEvents = "none";
						e.style.userSelect = "none";
					});
				});
			}

			this.$nextTick(() => {
				//Force sizing to happen on window load
				window.dispatchEvent(new Event("resize"));
			});
		},

		initInfinite(): void {
			const track = this.$refs.sliderItems as HTMLElement;
			if (!track) return;
			this.frag = Array.from(this.$refs.sliderItems.children) as HTMLElement[];

			if (this.frag.length === 0) return;

			this.frag.forEach((el, i) => {
				const clone = createGhostClone(el, i);
				el.setAttribute("data-slide-index", `${i}`);
				this.clones.push(clone);
			});

			this.addClones();
		},

		addClones() {
			const track = this.$refs.sliderItems as HTMLElement;
			const left = document.createDocumentFragment();
			const right = document.createDocumentFragment();

			this.clones.forEach((el) => {
				left.appendChild(el.cloneNode(true));
				right.appendChild(el.cloneNode(true));
			});

			track.insertBefore(left, track.firstChild);
			track.appendChild(right);

			if (this.shouldAddClones()) {
				this.addClones();
			}
		},

		shouldAddClones(): boolean {
			if (!settings.infinite) return false;
			return (
				this.$refs.sliderItems.getBoundingClientRect().width / 3 <
				this.$refs.slider.getBoundingClientRect().width
			);
		},

		getClosestSlide(): Object | undefined {
			const left = this.$refs.slider.scrollLeft + this.sliderOffset;
			const slide = this.slides.find((i) => {
				const half = i.width / 2;
				if (left < i.start + half) return true;
				else return i.start >= left;
			});
			return slide ?? undefined;
		},

		setSizes(items: HTMLElement): void {
			this.slides = Array.from(items.children).map((i) => {
				return {
					width: (i as HTMLElement).getBoundingClientRect().width,
					start: (i as HTMLElement).offsetLeft,
					trueSlide: i.hasAttribute("data-slide-index"),
				};
			});

			this.sliderOffset = this.$el.getBoundingClientRect().x;
			this.width = document.documentElement.clientWidth;
			this.trackWidth = this.$refs.sliderItems.getBoundingClientRect().width;
			this.trackEnd =
				this.trackWidth - this.$refs.slider.getBoundingClientRect().width - 1;

			if (!settings.infinite) {
				const paddingLeft = this.$el.getBoundingClientRect().x;
				const paddingRight =
					this.width -
					(this.$el.getBoundingClientRect().x +
						this.$el.getBoundingClientRect().width);
				// padding on first and last child, to line up with content container and be pretty ✨
				items.children[0].style.paddingLeft = `${paddingLeft}px`;
				items.children[items.children.length - 1].style.paddingRight =
					`${paddingRight}px`;
			} else {
				this.startRePos = this.slides.find((i) => i.trueSlide == true).start;
				const last = this.slides.findLast((i) => i.trueSlide == true);
				this.endRePos = last.start + last.width - this.width;
			}
		},

		setPosition(): void {
			requestAnimationFrame(() => {
				if (this.dragging) {
					const s = this.$refs.slider;
					const modifier = this.mouse.x - this.downPoint;
					let point = this.currentScrollPoint - modifier;

					// infinite repositioning
					if (settings.infinite) {
						if (s.scrollLeft <= 1) point = this.startRePos;
						if (s.scrollLeft >= this.trackEnd) point = this.endRePos;
						this.currentScrollPoint = point + modifier;
					}

					s.scrollTo({
						left: point,
						behavior: "instant",
					});
				}

				this.setPosition();
			});
		},

		setPromptPosition(): void {
			const lerpFactor = 0.15; // Adjust this value to change the smoothness (0.1 = smooth, 1 = instant)
			const lerp = (start, end) => start * (1 - lerpFactor) + end * lerpFactor;

			this.prompt.x = Math.round(lerp(this.prompt.x, this.mouse.x));
			this.prompt.y = Math.round(lerp(this.prompt.y, this.mouse.y));

			requestAnimationFrame(() => this.setPromptPosition());
		},

		setMouseInternalPosition(event: MouseEvent): void {
			const rect = this.$root.getBoundingClientRect();
			this.mouse.x = Math.round(event.clientX - rect.left);
			this.mouse.y = Math.round(event.clientY - rect.top);
		},

		handleInitDrag(event: MouseEvent | TouchEvent): void {
			this.downPoint = event.clientX ?? event?.touches[0].clientX;
			this.currentScrollPoint = this.$refs.slider.scrollLeft;
		},

		handleDrag(event: MouseEvent | TouchEvent): void {
			this.setMouseInternalPosition(event);
			this.mouse.x = event.clientX ?? event.touches[0].clientX;
			if (this.downPoint) {
				this.dragging = true;
			}
		},

		handleCancelDrag(event): void {
			if (this.dragging && this.isFirefox) event.preventDefault();
			this.dragging = false;
			this.downPoint = 0;

			const slide = this.getClosestSlide();

			if (this.$refs.slider.scrollLeft < this.trackEnd) {
				this.handleScrollSnap(slide.start);
			}
		},

		handleScrollSnap(start: number, behavior = "smooth"): void {
			this.$refs.slider.scrollTo({
				left: start - this.sliderOffset,
				behavior,
			});
		},

		handleNext(): void {
			const slide = this.getClosestSlide();
			const index = this.slides.indexOf(slide) + 1;
			if (slide && index <= this.slides.length - 1) {
				this.handleScrollSnap(this.slides[index]?.start);
			}
		},

		handlePrev(): void {
			const slide = this.getClosestSlide();
			const index = this.slides.indexOf(slide) - 1;
			if (slide && index >= 0) {
				this.handleScrollSnap(this.slides[index]?.start);
			}
		},
	};
};

Alpine.data("slider", slider);
